/**
 * @lodex-pro/web-portal/routes/TankRegistrySections — the company's own tank records and its
 * batteries on the Tank setup page (tank registry PR 1, migration 0245).
 *
 * Corbin's rules (13.8, D-20/D-22): a tank's PRODUCER TANK ID (optional, e.g. a lease tank
 * number) identifies it, unique across the company among active tanks; machine readings use it.
 * Its NAME is unique only at its well, site or battery: names repeat across wells. A tank
 * sits on exactly one of a catalog well, a free-text site or one of the company's batteries. Its
 * sizes (capacity, height, bbl per inch, type) are the company's; each hauler keeps its own, and
 * neither overwrites the other, so the hauler's sizes show next to the company's (read-only),
 * marked when they differ. A tank can carry several tags (SCADA, MQTT, other). "Needs size": a
 * reading was given in inches and the tank has no bbl per inch to turn it into barrels.
 *
 * Only OPERATOR_ADMIN edits; a foreman sees the same lists without controls. The API checks the
 * role on every write (MANAGE_OPERATOR_TANKS); this is the UX. Errors are said in the words of
 * the API error catalog (portalErrorText).
 *
 * DEC-379: the form offers the standard tank sizes (/tank-setup/standard-tanks), each labelled
 * with its size, so the two 500 BBL tanks (15 ft 6 in and 15 ft 6.5 in across) are separate
 * choices; picking one fills capacity, height and bbl per inch and links the tank to it. A
 * capacity the catalog has more than one size for, with no bbl per inch and no size picked, asks
 * the user to pick one: it is never guessed (DEC-377).
 */

import React from 'react';
import {
  TANK_TAG_SYSTEMS,
  type BatteryView,
  type CompanyWellView,
  type RegistryTankLinkView,
  type RegistryTankView,
  type TankTagSystem,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { standardTankLabel, suggestBblPerInch } from '@lodex-pro/shared/tank-strapping';
import {
  useAddTankTag,
  useBatteries,
  useCompanyWells,
  useRegistryTanks,
  useRemoveTankTag,
  useSaveBattery,
  useSaveRegistryTank,
  useSetBatteryWells,
  useStandardTankOptions,
  useUnlinkTank,
} from '../hooks/useTankSetupAndRoutes';
import { PortalDataError } from '../components/PortalDataError';
import { portalErrorText } from '../components/portal-error-text';
import {
  BUTTON,
  CARD,
  INPUT,
  MUTED,
  PRIMARY_BUTTON,
  TAG,
  WARNING,
  capacityLabel,
  decimalLabel,
  plural,
  registryPlaceLabel,
  sizesDiffer,
} from './operator-tank-labels';

const ERROR_TEXT: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '13px',
  margin: '6px 0 0',
};

function wellLabel(w: {
  readonly wellName: string | null;
  readonly apiNumber: string | null;
}): string {
  const name = w.wellName !== null && w.wellName.trim() !== '' ? w.wellName : 'Unnamed well';
  return w.apiNumber !== null && w.apiNumber !== '' ? `${name} · API ${w.apiNumber}` : name;
}

// ── Your tanks ───────────────────────────────────────────────────────────────

export function RegistrySection({ isAdmin }: { readonly isAdmin: boolean }): React.JSX.Element {
  const registry = useRegistryTanks();
  const batteries = useBatteries();
  const wells = useCompanyWells();
  const [adding, setAdding] = React.useState(false);
  const tanks = [...(registry.data?.tanks ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const wellList = wells.data?.wells ?? [];
  const batteryList = batteries.data?.batteries ?? [];

  return (
    <section aria-label="Your tanks" style={{ marginBottom: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <h2 style={{ fontSize: '17px', marginBottom: '8px' }}>Your tanks</h2>
        {isAdmin && !adding && (
          <button
            type="button"
            style={BUTTON}
            onClick={(): void => {
              setAdding(true);
            }}
          >
            Add tank
          </button>
        )}
      </div>
      {adding && (
        <div style={{ ...CARD, marginBottom: '12px' }}>
          <TankForm
            tank={null}
            wells={wellList}
            batteries={batteryList}
            onDone={(): void => {
              setAdding(false);
            }}
          />
        </div>
      )}
      {registry.isLoading && <p role="status">Loading your tanks…</p>}
      {registry.isError && (
        <PortalDataError error={registry.error} fallback="Could not load your tanks." />
      )}
      {registry.data !== undefined &&
        (tanks.length === 0 ? (
          <p role="status" style={MUTED}>
            You have no tanks yet.
            {isAdmin ? ' Add one, or confirm your haulers’ records above.' : ''}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {tanks.map((t) => (
              <RegistryTankCard
                key={t.id}
                tank={t}
                isAdmin={isAdmin}
                wells={wellList}
                batteries={batteryList}
              />
            ))}
          </div>
        ))}
    </section>
  );
}

function SizeItem({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): React.JSX.Element {
  return (
    <span>
      <span style={MUTED}>{label}</span> {value ?? '—'}
    </span>
  );
}

function RegistryTankCard({
  tank: t,
  isAdmin,
  wells,
  batteries,
}: {
  readonly tank: RegistryTankView;
  readonly isAdmin: boolean;
  readonly wells: readonly CompanyWellView[];
  readonly batteries: readonly BatteryView[];
}): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const bpi = decimalLabel(t.bblPerInch);
  const height = decimalLabel(t.heightIn);

  return (
    <article aria-label={`Tank ${t.name}`} style={{ ...CARD, opacity: t.isActive ? 1 : 0.75 }}>
      {editing ? (
        <TankForm
          tank={t}
          wells={wells}
          batteries={batteries}
          onDone={(): void => {
            setEditing(false);
          }}
        />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '15px', margin: 0 }}>{t.name}</h3>
            <span style={{ ...MUTED, fontSize: '13px' }}>{registryPlaceLabel(t)}</span>
            {t.producerTankId != null && (
              <span style={{ fontSize: '13px' }} data-testid="producer-tank-id">
                <span style={MUTED}>Producer tank ID</span> {t.producerTankId}
              </span>
            )}
            <span style={TAG}>{t.isActive ? 'Active' : 'Inactive'}</span>
            {t.needsSize && (
              <span
                style={WARNING}
                title="A reading was entered in inches, but this tank has no bbl per inch to turn it into barrels."
              >
                Needs size
              </span>
            )}
            {isAdmin && (
              <button
                type="button"
                style={{ ...BUTTON, marginLeft: 'auto' }}
                aria-label={`Edit ${t.name}`}
                onClick={(): void => {
                  setEditing(true);
                }}
              >
                Edit
              </button>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
              fontSize: '13px',
              marginTop: '6px',
            }}
          >
            <SizeItem
              label="Capacity"
              value={t.capacityBbl === null ? null : capacityLabel(t.capacityBbl)}
            />
            <SizeItem label="Height" value={height === null ? null : `${height} in`} />
            <SizeItem label="BBL per inch" value={bpi} />
            <SizeItem label="Type" value={t.tankType} />
          </div>
        </>
      )}
      <TagsEditor tank={t} isAdmin={isAdmin} />
      {t.links.length === 0 ? (
        <p style={{ ...MUTED, fontSize: '13px', margin: '8px 0 0' }}>
          No hauler records linked to this tank.
        </p>
      ) : (
        <ul
          aria-label={`Haulers' records of ${t.name}`}
          style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}
        >
          {t.links.map((l) => (
            <HaulerLinkRow key={l.haulerTankId} tank={t} link={l} isAdmin={isAdmin} />
          ))}
        </ul>
      )}
    </article>
  );
}

// ── Add / edit form ──────────────────────────────────────────────────────────

type Placement = 'WELL' | 'SITE' | 'BATTERY';

/** '' is "not set"; anything else must be a number above 0 and at most `max`. */
function parseSize(raw: string, max: number): { ok: true; value: number | null } | { ok: false } {
  const s = raw.trim();
  if (s === '') return { ok: true, value: null };
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0 || n > max) return { ok: false };
  return { ok: true, value: n };
}

function sizeText(v: string | null): string {
  return v === null || v === '' ? '' : String(Number(v));
}

function TankForm({
  tank,
  wells,
  batteries,
  onDone,
}: {
  /** null = a new tank. */
  readonly tank: RegistryTankView | null;
  readonly wells: readonly CompanyWellView[];
  readonly batteries: readonly BatteryView[];
  readonly onDone: () => void;
}): React.JSX.Element {
  const save = useSaveRegistryTank();
  const [name, setName] = React.useState(tank?.name ?? '');
  // 13.8: sent only when changed, so an untouched field keeps the stored ID (the API's rule).
  const [producerTankId, setProducerTankId] = React.useState(tank?.producerTankId ?? '');
  const [placement, setPlacement] = React.useState<Placement>(
    tank === null
      ? wells.length > 0
        ? 'WELL'
        : 'SITE'
      : tank.wellId !== null
        ? 'WELL'
        : tank.batteryId !== null
          ? 'BATTERY'
          : 'SITE',
  );
  const [wellId, setWellId] = React.useState(tank?.wellId ?? '');
  const [siteLabel, setSiteLabel] = React.useState(tank?.siteLabel ?? '');
  const [batteryId, setBatteryId] = React.useState(tank?.batteryId ?? '');
  const [capacity, setCapacity] = React.useState(sizeText(tank?.capacityBbl ?? null));
  const [height, setHeight] = React.useState(sizeText(tank?.heightIn ?? null));
  const [bpi, setBpi] = React.useState(sizeText(tank?.bblPerInch ?? null));
  const [tankType, setTankType] = React.useState(tank?.tankType ?? '');
  const [standardId, setStandardId] = React.useState(tank?.standardTankCatalogId ?? '');
  const [isActive, setIsActive] = React.useState(tank?.isActive ?? true);
  const sizeOptions = useStandardTankOptions().data?.standardTanks ?? [];

  const pickSize = (id: string): void => {
    setStandardId(id);
    const o = sizeOptions.find((x) => x.id === id);
    if (o === undefined) return;
    setCapacity(sizeText(o.capacityBbl));
    setBpi(sizeText(o.bblPerInch));
    if (o.heightFt !== null) setHeight(String(Math.round(Number(o.heightFt) * 12 * 100) / 100));
  };
  // A capacity with more than one standard size, no size picked and no bbl per inch: ask.
  const sizesAtCapacity =
    standardId === '' && bpi.trim() === ''
      ? suggestBblPerInch({ capacityBbl: capacity.trim() }, sizeOptions).choices
      : [];
  const [error, setError] = React.useState<string | null>(null);

  // An inactive battery stays choosable only for the tank already on it.
  const batteryOptions = batteries.filter((b) => b.isActive || b.id === tank?.batteryId);
  const sortedWells = [...wells].sort((a, b) => wellLabel(a).localeCompare(wellLabel(b)));

  const submit = async (): Promise<void> => {
    setError(null);
    const nextName = name.trim();
    if (nextName === '') {
      setError('Give the tank a name.');
      return;
    }
    const site = siteLabel.trim();
    if (placement === 'WELL' && wellId === '') {
      setError('Choose the well the tank sits on.');
      return;
    }
    if (placement === 'SITE' && site === '') {
      setError('Name the site the tank sits on.');
      return;
    }
    if (placement === 'BATTERY' && batteryId === '') {
      setError('Choose the battery the tank sits on.');
      return;
    }
    const cap = parseSize(capacity, 100000);
    const h = parseSize(height, 2000);
    const b = parseSize(bpi, 1000);
    if (!cap.ok) {
      setError('Capacity must be a number of barrels above 0, or left empty.');
      return;
    }
    if (!h.ok) {
      setError('Height must be a number of inches above 0, or left empty.');
      return;
    }
    if (!b.ok) {
      setError('BBL per inch must be a number above 0, or left empty.');
      return;
    }
    const type = tankType.trim();
    const pid = producerTankId.trim();
    const pidChanged =
      tank === null ? pid !== '' : pid.toUpperCase() !== (tank.producerTankId ?? '').toUpperCase();
    try {
      await save.mutateAsync({
        ...(tank === null ? {} : { id: tank.id }),
        name: nextName,
        ...(pidChanged ? { producerTankId: pid === '' ? null : pid } : {}),
        // Exactly one placement; the other two are cleared (an edit is the full record).
        wellId: placement === 'WELL' ? wellId : null,
        siteLabel: placement === 'SITE' ? site : null,
        batteryId: placement === 'BATTERY' ? batteryId : null,
        capacityBbl: cap.value,
        heightIn: h.value,
        bblPerInch: b.value,
        tankType: type === '' ? null : type,
        standardTankCatalogId: standardId === '' ? null : standardId,
        ...(tank === null ? {} : { isActive }),
      });
      onDone();
    } catch (err) {
      setError(portalErrorText(err, 'Could not save that tank. Try again.'));
    }
  };

  const field: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '13px',
  };

  return (
    <form
      aria-label={tank === null ? 'Add a tank' : `Edit ${tank.name}`}
      onSubmit={(e): void => {
        e.preventDefault();
        void submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <label style={field}>
        Tank name (unique at its well, site or battery)
        <input
          aria-label="Tank name"
          value={name}
          maxLength={120}
          onChange={(e): void => {
            setName(e.target.value);
          }}
          style={INPUT}
        />
      </label>
      <label style={field}>
        Producer tank ID (optional)
        <input
          aria-label="Producer tank ID"
          aria-describedby={`producer-tank-id-help-${tank?.id ?? 'new'}`}
          value={producerTankId}
          maxLength={120}
          onChange={(e): void => {
            setProducerTankId(e.target.value);
          }}
          style={INPUT}
        />
        <span
          id={`producer-tank-id-help-${tank?.id ?? 'new'}`}
          style={{ ...MUTED, fontSize: '12px' }}
        >
          Your company&rsquo;s ID for this tank, e.g. a lease tank number. Readings sent by MQTT or
          the API use it.
        </span>
      </label>
      <fieldset
        style={{
          border: '1px solid var(--color-border-primary)',
          borderRadius: '12px',
          padding: '8px 12px',
        }}
      >
        <legend style={{ fontSize: '13px' }}>Where it sits</legend>
        <div style={{ display: 'flex', gap: '16px', fontSize: '13px', marginBottom: '8px' }}>
          {(
            [
              ['WELL', 'Well'],
              ['SITE', 'Site'],
              ['BATTERY', 'Battery'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input
                type="radio"
                name={`placement-${tank?.id ?? 'new'}`}
                value={value}
                checked={placement === value}
                onChange={(): void => {
                  setPlacement(value);
                }}
              />
              {label}
            </label>
          ))}
        </div>
        {placement === 'WELL' && (
          <select
            aria-label="Well"
            value={wellId}
            onChange={(e): void => {
              setWellId(e.target.value);
            }}
            style={INPUT}
          >
            <option value="">Choose a well…</option>
            {sortedWells.map((w) => (
              <option key={w.wellId} value={w.wellId}>
                {wellLabel(w)}
              </option>
            ))}
          </select>
        )}
        {placement === 'SITE' && (
          <input
            aria-label="Site"
            placeholder="e.g. East yard"
            value={siteLabel}
            maxLength={160}
            onChange={(e): void => {
              setSiteLabel(e.target.value);
            }}
            style={INPUT}
          />
        )}
        {placement === 'BATTERY' &&
          (batteryOptions.length === 0 ? (
            <p style={{ ...MUTED, fontSize: '13px', margin: 0 }}>
              You have no active batteries. Add one under Batteries below.
            </p>
          ) : (
            <select
              aria-label="Battery"
              value={batteryId}
              onChange={(e): void => {
                setBatteryId(e.target.value);
              }}
              style={INPUT}
            >
              <option value="">Choose a battery…</option>
              {batteryOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.isActive ? '' : ' (inactive)'}
                </option>
              ))}
            </select>
          ))}
      </fieldset>
      <label style={field}>
        Standard size (optional; fills the sizes below)
        <select
          aria-label="Standard size"
          value={standardId}
          onChange={(e): void => {
            pickSize(e.target.value);
          }}
          style={INPUT}
        >
          <option value="">None</option>
          {sizeOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {standardTankLabel(o)}
            </option>
          ))}
          {standardId !== '' && !sizeOptions.some((o) => o.id === standardId) && (
            <option value={standardId}>A size no longer in the catalog</option>
          )}
        </select>
      </label>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <label style={field}>
          Capacity (BBL)
          <input
            aria-label="Capacity (BBL)"
            inputMode="decimal"
            value={capacity}
            onChange={(e): void => {
              setCapacity(e.target.value);
            }}
            style={{ ...INPUT, width: '120px' }}
          />
        </label>
        <label style={field}>
          Height (in)
          <input
            aria-label="Height (in)"
            inputMode="decimal"
            value={height}
            onChange={(e): void => {
              setHeight(e.target.value);
            }}
            style={{ ...INPUT, width: '120px' }}
          />
        </label>
        <label style={field}>
          BBL per inch
          <input
            aria-label="BBL per inch"
            inputMode="decimal"
            value={bpi}
            onChange={(e): void => {
              setBpi(e.target.value);
            }}
            style={{ ...INPUT, width: '120px' }}
          />
        </label>
        <label style={field}>
          Tank type
          <input
            aria-label="Tank type"
            placeholder="e.g. GUN BARREL or WATER"
            value={tankType}
            maxLength={60}
            onChange={(e): void => {
              setTankType(e.target.value);
            }}
            style={{ ...INPUT, width: '160px' }}
          />
        </label>
      </div>
      {sizesAtCapacity.length > 1 && (
        <p role="status" style={{ ...WARNING, fontSize: '13px', margin: 0 }}>
          {capacity.trim()} BBL comes in {sizesAtCapacity.length} standard sizes. Pick one under
          Standard size, or type its BBL per inch:{' '}
          {sizesAtCapacity.map((c) => `${c.name} (${sizeText(c.bblPerInch)} BBL/in)`).join(' or ')}.
        </p>
      )}
      {tank !== null && (
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e): void => {
              setIsActive(e.target.checked);
            }}
          />
          Active
        </label>
      )}
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" style={PRIMARY_BUTTON} disabled={save.isPending}>
          {tank === null ? 'Add tank' : 'Save'}
        </button>
        <button type="button" style={BUTTON} onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Tags ─────────────────────────────────────────────────────────────────────

const SYSTEM_LABEL: Record<TankTagSystem, string> = {
  SCADA: 'SCADA',
  MQTT: 'MQTT',
  OTHER: 'Other',
};

function TagsEditor({
  tank: t,
  isAdmin,
}: {
  readonly tank: RegistryTankView;
  readonly isAdmin: boolean;
}): React.JSX.Element | null {
  const add = useAddTankTag();
  const remove = useRemoveTankTag();
  const [system, setSystem] = React.useState<TankTagSystem>('SCADA');
  const [tag, setTag] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  if (!isAdmin && t.tags.length === 0) return null;

  const addTag = async (): Promise<void> => {
    setError(null);
    const value = tag.trim();
    if (value === '') {
      setError('Type the tag first.');
      return;
    }
    try {
      await add.mutateAsync({ tankId: t.id, system, tag: value });
      setTag('');
    } catch (err) {
      setError(portalErrorText(err, 'Could not add that tag. Try again.'));
    }
  };

  const removeTag = async (tagId: string): Promise<void> => {
    setError(null);
    try {
      await remove.mutateAsync({ tagId });
    } catch (err) {
      setError(portalErrorText(err, 'Could not remove that tag. Try again.'));
    }
  };

  return (
    <div style={{ marginTop: '8px', fontSize: '13px' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={MUTED}>Tags</span>
        {t.tags.length === 0 && <span style={MUTED}>none</span>}
        <ul
          aria-label={`Tags of ${t.name}`}
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'contents' }}
        >
          {t.tags.map((g) => (
            <li key={g.id} style={{ ...TAG, display: 'inline-flex', gap: '6px' }}>
              <span>
                {SYSTEM_LABEL[g.system]}: {g.tag}
              </span>
              {isAdmin && (
                <button
                  type="button"
                  aria-label={`Remove ${SYSTEM_LABEL[g.system]} tag ${g.tag}`}
                  disabled={remove.isPending}
                  onClick={(): void => {
                    void removeTag(g.id);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-danger)',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
          <select
            aria-label={`Tag system for ${t.name}`}
            value={system}
            onChange={(e): void => {
              setSystem(e.target.value as TankTagSystem);
            }}
            style={INPUT}
          >
            {TANK_TAG_SYSTEMS.map((s) => (
              <option key={s} value={s}>
                {SYSTEM_LABEL[s]}
              </option>
            ))}
          </select>
          <input
            className="keep-case"
            aria-label={`New tag for ${t.name}`}
            placeholder="Tag or topic"
            value={tag}
            maxLength={200}
            onChange={(e): void => {
              setTag(e.target.value);
            }}
            style={{ ...INPUT, width: '200px' }}
          />
          <button
            type="button"
            style={BUTTON}
            disabled={add.isPending}
            onClick={(): void => {
              void addTag();
            }}
          >
            Add tag
          </button>
        </div>
      )}
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
    </div>
  );
}

// ── A hauler's record of the tank ────────────────────────────────────────────

function HaulerLinkRow({
  tank,
  link: l,
  isAdmin,
}: {
  readonly tank: RegistryTankView;
  readonly link: RegistryTankLinkView;
  readonly isAdmin: boolean;
}): React.JSX.Element {
  const unlink = useUnlinkTank();
  const [asking, setAsking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const capDiffers = sizesDiffer(l.capacityBbl, tank.capacityBbl);
  const bpiDiffers = sizesDiffer(l.bblPerInch, tank.bblPerInch);
  const bpi = decimalLabel(l.bblPerInch);

  const doUnlink = async (): Promise<void> => {
    setError(null);
    try {
      await unlink.mutateAsync({ haulerTankId: l.haulerTankId });
      setAsking(false);
    } catch (err) {
      setError(portalErrorText(err, 'Could not unlink that record. Try again.'));
    }
  };

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '6px 0',
        borderTop: '1px solid var(--color-border-primary)',
        fontSize: '13px',
      }}
    >
      <span style={{ minWidth: '160px' }}>{l.haulerName}</span>
      <span>
        calls it <q>{l.haulerTankName}</q>
      </span>
      <span style={MUTED}>
        {l.linkedBy === 'MATCH' ? 'linked automatically' : 'linked by hand'}
      </span>
      <span style={MUTED} aria-label={`${l.haulerName}'s sizes`}>
        their sizes: {capacityLabel(l.capacityBbl)} ·{' '}
        {bpi === null ? 'BBL per inch not set' : `${bpi} BBL/in`}
      </span>
      {(capDiffers || bpiDiffers) && (
        <span
          style={WARNING}
          title={`${l.haulerName}'s ${[
            capDiffers ? 'capacity' : null,
            bpiDiffers ? 'bbl per inch' : null,
          ]
            .filter((x) => x !== null)
            .join(' and ')} differ from yours. Neither overwrites the other.`}
        >
          differs:{' '}
          {[capDiffers ? 'capacity' : null, bpiDiffers ? 'BBL/in' : null]
            .filter((x) => x !== null)
            .join(', ')}
        </span>
      )}
      {isAdmin && (
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          {asking ? (
            <>
              <span>
                Unlink {l.haulerName}&rsquo;s record from {tank.name}?
              </span>
              <button
                type="button"
                style={PRIMARY_BUTTON}
                disabled={unlink.isPending}
                onClick={(): void => {
                  void doUnlink();
                }}
              >
                Yes, unlink
              </button>
              <button
                type="button"
                style={BUTTON}
                onClick={(): void => {
                  setAsking(false);
                }}
              >
                Keep it
              </button>
            </>
          ) : (
            <button
              type="button"
              style={BUTTON}
              aria-label={`Unlink ${l.haulerName}'s ${l.haulerTankName}`}
              onClick={(): void => {
                setAsking(true);
              }}
            >
              Unlink
            </button>
          )}
        </span>
      )}
      {error !== null && (
        <span role="alert" style={{ color: 'var(--color-danger)', width: '100%' }}>
          {error}
        </span>
      )}
    </li>
  );
}

// ── Batteries ────────────────────────────────────────────────────────────────

export function BatteriesSection({ isAdmin }: { readonly isAdmin: boolean }): React.JSX.Element {
  const batteries = useBatteries();
  const wells = useCompanyWells();
  const save = useSaveBattery();
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const list = [...(batteries.data?.batteries ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const addBattery = async (): Promise<void> => {
    setError(null);
    const value = name.trim();
    if (value === '') {
      setError('Give the battery a name.');
      return;
    }
    try {
      await save.mutateAsync({ name: value });
      setName('');
    } catch (err) {
      setError(portalErrorText(err, 'Could not add that battery. Try again.'));
    }
  };

  return (
    <section aria-label="Batteries">
      <h2 style={{ fontSize: '17px', marginBottom: '4px' }}>Batteries</h2>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0 }}>
        A battery is a group of tanks that serves one or more wells.
      </p>
      {isAdmin && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
          <input
            aria-label="New battery name"
            placeholder="Battery name"
            value={name}
            maxLength={120}
            onChange={(e): void => {
              setName(e.target.value);
            }}
            style={INPUT}
          />
          <button
            type="button"
            style={BUTTON}
            disabled={save.isPending}
            onClick={(): void => {
              void addBattery();
            }}
          >
            Add battery
          </button>
          {error !== null && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
              {error}
            </span>
          )}
        </div>
      )}
      {batteries.isLoading && <p role="status">Loading batteries…</p>}
      {batteries.isError && (
        <PortalDataError error={batteries.error} fallback="Could not load batteries." />
      )}
      {batteries.data !== undefined &&
        (list.length === 0 ? (
          <p role="status" style={MUTED}>
            You have no batteries yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {list.map((b) => (
              <BatteryCard
                key={b.id}
                battery={b}
                isAdmin={isAdmin}
                wells={wells.data?.wells ?? []}
              />
            ))}
          </div>
        ))}
    </section>
  );
}

function BatteryCard({
  battery: b,
  isAdmin,
  wells,
}: {
  readonly battery: BatteryView;
  readonly isAdmin: boolean;
  readonly wells: readonly CompanyWellView[];
}): React.JSX.Element {
  const save = useSaveBattery();
  const setWells = useSetBatteryWells();
  const [renaming, setRenaming] = React.useState(false);
  const [name, setName] = React.useState(b.name);
  const [choosing, setChoosing] = React.useState(false);
  const [chosen, setChosen] = React.useState<ReadonlySet<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);

  const saveBattery = async (nextName: string, isActive: boolean): Promise<boolean> => {
    setError(null);
    if (nextName.trim() === '') {
      setError('Give the battery a name.');
      return false;
    }
    try {
      await save.mutateAsync({ id: b.id, name: nextName.trim(), isActive });
      return true;
    } catch (err) {
      setError(portalErrorText(err, 'Could not save that battery. Try again.'));
      return false;
    }
  };

  const saveWells = async (): Promise<void> => {
    setError(null);
    try {
      await setWells.mutateAsync({ id: b.id, wellIds: [...chosen] });
      setChoosing(false);
    } catch (err) {
      setError(portalErrorText(err, 'Could not set the battery’s wells. Try again.'));
    }
  };

  const sortedWells = [...wells].sort((x, y) => wellLabel(x).localeCompare(wellLabel(y)));

  return (
    <article aria-label={`Battery ${b.name}`} style={{ ...CARD, opacity: b.isActive ? 1 : 0.75 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        {renaming ? (
          <>
            <input
              aria-label="Battery name"
              value={name}
              maxLength={120}
              onChange={(e): void => {
                setName(e.target.value);
              }}
              style={INPUT}
            />
            <button
              type="button"
              style={PRIMARY_BUTTON}
              disabled={save.isPending}
              onClick={(): void => {
                void saveBattery(name, b.isActive).then((ok) => {
                  if (ok) setRenaming(false);
                });
              }}
            >
              Save
            </button>
            <button
              type="button"
              style={BUTTON}
              onClick={(): void => {
                setRenaming(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <h3 style={{ fontSize: '15px', margin: 0 }}>{b.name}</h3>
        )}
        <span style={TAG}>{b.isActive ? 'Active' : 'Inactive'}</span>
        <span style={{ ...MUTED, fontSize: '13px' }}>{plural(b.tankCount, 'tank')}</span>
        {isAdmin && !renaming && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
            <button
              type="button"
              style={BUTTON}
              aria-label={`Rename ${b.name}`}
              onClick={(): void => {
                setName(b.name);
                setRenaming(true);
              }}
            >
              Rename
            </button>
            <button
              type="button"
              style={BUTTON}
              aria-label={`${b.isActive ? 'Deactivate' : 'Reactivate'} ${b.name}`}
              disabled={save.isPending}
              onClick={(): void => {
                void saveBattery(b.name, !b.isActive);
              }}
            >
              {b.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
            <button
              type="button"
              style={BUTTON}
              aria-label={`Set wells of ${b.name}`}
              onClick={(): void => {
                setChosen(new Set(b.wells.map((w) => w.wellId)));
                setChoosing(true);
              }}
            >
              Set wells
            </button>
          </span>
        )}
      </div>
      {choosing ? (
        <fieldset
          style={{
            border: '1px solid var(--color-border-primary)',
            borderRadius: '12px',
            padding: '8px 12px',
            marginTop: '8px',
          }}
        >
          <legend style={{ fontSize: '13px' }}>Wells {b.name} serves</legend>
          {sortedWells.length === 0 ? (
            <p style={{ ...MUTED, fontSize: '13px', margin: 0 }}>Your company has no wells.</p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                maxHeight: '240px',
                overflowY: 'auto',
                fontSize: '13px',
              }}
            >
              {sortedWells.map((w) => (
                <label key={w.wellId} style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="checkbox"
                    checked={chosen.has(w.wellId)}
                    onChange={(e): void => {
                      const on = e.target.checked;
                      setChosen((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(w.wellId);
                        else next.delete(w.wellId);
                        return next;
                      });
                    }}
                  />
                  {wellLabel(w)}
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              type="button"
              style={PRIMARY_BUTTON}
              disabled={setWells.isPending}
              onClick={(): void => {
                void saveWells();
              }}
            >
              Save wells
            </button>
            <button
              type="button"
              style={BUTTON}
              onClick={(): void => {
                setChoosing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </fieldset>
      ) : b.wells.length === 0 ? (
        <p style={{ ...MUTED, fontSize: '13px', margin: '8px 0 0' }}>No wells yet.</p>
      ) : (
        <ul
          aria-label={`Wells of ${b.name}`}
          style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '13px' }}
        >
          {b.wells.map((w) => (
            <li key={w.wellId}>{wellLabel(w)}</li>
          ))}
        </ul>
      )}
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
    </article>
  );
}
